import 'react-datepicker/dist/react-datepicker.css';

import { cs, enGB, sk } from 'date-fns/locale';
import { type ChangeEvent, type InputHTMLAttributes, useEffect, useMemo } from 'react';
import { DatePicker } from 'utils/datePickerImport';
import type { FieldValues, Path, RegisterOptions } from 'react-hook-form';
import { Controller, useFormContext } from 'react-hook-form';
import { isValid, parse, parseISO, set } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { Typography } from '@mui/material';
import { DatePickerWrapper, StyledDatePicker } from './DynamicDateInput.styles';
import type { FormItemUiDefinition } from '../../../types/formItem';
import { getUIDefinitionIconStyles, getUiDefinitionTextStyles } from '../../../utils/styles';
import { getIcon } from '../../../utils/common';
import { getDateFormat } from 'utils/dateAndTime';

interface DateInputProps<T extends FieldValues> extends InputHTMLAttributes<HTMLInputElement> {
  registerName: Path<T>;
  registerProps?: RegisterOptions<T, Path<T>> & {
    minDate?: Date;
    maxDate?: Date;
    dateMask?: DateMask;
  };
  error?: FieldValues['errors'];
  lightError?: boolean;
  onDateChange?: () => void;
  onBlur?: () => void;
  formItemUiDefinition?: FormItemUiDefinition;
}

type DateMask = {
  Year: boolean;
  Month: boolean;
  Day: boolean;
  Time: boolean;
};

export const DynamicDateInput = <T extends FieldValues>({
  registerName,
  registerProps,
  error,
  lightError,
  disabled = false,
  onDateChange,
  onBlur: _onBlur,
  formItemUiDefinition,
}: DateInputProps<T>) => {
  const { control, setError, clearErrors, watch } = useFormContext<T>();
  const { dateMask, minDate, maxDate } = registerProps as RegisterOptions<T, Path<T>> & { minDate?: Date; maxDate?: Date; dateMask?: DateMask };
  const { t, i18n } = useTranslation();
  const inputStyles = getUiDefinitionTextStyles(formItemUiDefinition);
  const Icon = formItemUiDefinition?.icon ? getIcon(formItemUiDefinition.icon) : null;

  const locale = useMemo(() => {
    switch (i18n.language) {
      case 'cs':
        return cs;
      case 'sk':
        return sk;
      case 'en':
        return enGB;
      default:
        return cs;
    }
  }, [i18n.language]);

  const dateFormat = useMemo(() => getDateFormat(dateMask), [dateMask]);

  const showFullMonthYearPicker = useMemo(() => dateMask?.Year && dateMask?.Month && dateMask?.Day, [dateMask]);
  const showMonthYearPicker = useMemo(() => !dateMask?.Year && dateMask?.Month && !dateMask?.Day, [dateMask]);
  const showYearPicker = useMemo(() => dateMask?.Year && !dateMask?.Month && !dateMask?.Day, [dateMask]);

  const shouldSetDefaultTime = useMemo(() => {
    return !dateMask?.Time && (dateMask?.Day || dateMask?.Month || dateMask?.Year);
  }, [dateMask]);

  const parseDate = (dateString: string): Date | null => {
    if (!dateString.trim()) return null;
    const isoDate = parseISO(dateString);
    if (isValid(isoDate)) {
      return isoDate;
    }

    const parsedDate = parse(dateString, dateFormat, new Date(), { locale });
    if (isValid(parsedDate)) {
      return parsedDate;
    }

    return null;
  };

  const setDefaultTimeIfNeeded = (date: Date | null): Date | null => {
    if (date && shouldSetDefaultTime) {
      return set(new Date(date), { hours: 12, minutes: 0, seconds: 0, milliseconds: 0 });
    }
    return date;
  };

  const handleChange = (
    e?: React.MouseEvent<HTMLElement, MouseEvent> | React.KeyboardEvent<HTMLElement> | ChangeEvent<HTMLInputElement>,
    onChange?: (date: string | null) => void
  ) => {
    const target = e?.target as HTMLInputElement;
    const dateValue = target?.value;

    if (dateValue && dateValue.trim() !== '') {
      const parsedDate = parseDate(dateValue);
      const dateWithDefaultTime = setDefaultTimeIfNeeded(parsedDate);

      if (dateWithDefaultTime && isValid(dateWithDefaultTime)) {
        clearErrors(registerName);
        onChange?.(dateWithDefaultTime.toISOString());
        onDateChange?.();
      } else {
        setError(registerName, { type: 'manual', message: '' });
        onChange?.(null);
      }
    }
  };

  useEffect(() => {
    if (watch(registerName) === null) {
      clearErrors(registerName);
    }
  }, [clearErrors, registerName, watch]);

  return (
    <>
      <StyledDatePicker hasError={!!error || Boolean(lightError)}>
        <Controller
          control={control}
          name={registerName}
          rules={registerProps}
          render={({ field: { onChange, onBlur, value } }) => (
            <DatePickerWrapper
              inputStyles={inputStyles}
              backgroundColor={formItemUiDefinition?.attributeColor}
              borderColor={formItemUiDefinition?.columnBorderColor}
              hasError={!!error || Boolean(lightError)}
              lightError={lightError}
              hasIcon={Boolean(Icon)}>
              <DatePicker
                locale={locale}
                disabled={disabled}
                autoComplete="off"
                timeFormat="HH:mm"
                placeholderText=""
                timeIntervals={1}
                dateFormat={dateFormat}
                showFullMonthYearPicker={showFullMonthYearPicker}
                showMonthYearPicker={showMonthYearPicker}
                showYearPicker={showYearPicker}
                showMonthDropdown={dateMask?.Month}
                showYearDropdown={dateMask?.Year}
                showTimeSelect={dateMask?.Time}
                showTimeSelectOnly={dateMask?.Time && !dateMask?.Day && !dateMask?.Month && !dateMask?.Year}
                minDate={minDate}
                maxDate={maxDate}
                isClearable
                selected={value ? new Date(value) : null}
                onChange={(date: Date | null) => {
                  const dateWithDefaultTime = setDefaultTimeIfNeeded(date);
                  onChange(dateWithDefaultTime?.toISOString());
                  onDateChange?.();
                  clearErrors(registerName);
                }}
                onBlur={() => {
                  onBlur();
                  _onBlur?.();
                }}
                onChangeRaw={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e, onChange)}
                strictParsing={false}
                allowSameDay={true}
                withPortal
                showIcon={Boolean(Icon)}
                icon={Icon ? <Icon sx={getUIDefinitionIconStyles(formItemUiDefinition)} /> : undefined}
              />
            </DatePickerWrapper>
          )}
        />
      </StyledDatePicker>
      {error && (
        <Typography variant="body1" color="error">
          {error.type === 'nullable' ? t('common.noCorrectPattern') : error.message}
        </Typography>
      )}
    </>
  );
};
